import { useState } from "react";
import { api } from "./api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { useResource, SectionHeader, Loading, Modal, ConfirmDialog, Field, PrimaryBtn, GhostBtn, inputCls } from "./components";

const EMPTY = { opponent: "", date: "", time: "4:00 PM", level: "Varsity", ha: "Home", location: "", wl: "TBD", scoreHome: "", scoreAway: "" };

const fmt = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso || "";
  return new Date(iso.slice(0, 10) + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};

const toForm = (g) => {
  const [h, a] = (g.score || "").split("-");
  return { opponent: g.opponent || "", date: g.date || "", time: g.time || "", level: g.level || "Varsity", ha: g.ha || "Home", location: g.location || "", wl: g.wl || "TBD", scoreHome: h ?? "", scoreAway: a ?? "" };
};

export default function GamesSection() {
  const { items, loading, reload } = useResource("games");
  const [editing, setEditing] = useState(null); // null | {id?, ...form}
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const sorted = [...items].sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  const save = async () => {
    if (!editing.opponent.trim() || !editing.date) {
      toast.error("Please enter the opponent and the game date.");
      return;
    }
    setSaving(true);
    const payload = {
      opponent: editing.opponent.trim(),
      date: editing.date,
      time: editing.time,
      level: editing.level,
      ha: editing.ha,
      location: editing.location,
      wl: editing.wl,
      score: editing.wl === "TBD" ? "" : `${editing.scoreHome || 0}-${editing.scoreAway || 0}`,
    };
    try {
      if (editing.id) {
        await api.put(`/admin/games/${editing.id}`, payload);
      } else {
        await api.post("/admin/games", payload);
      }
      toast.success("Game saved! The website schedule is updated.");
      setEditing(null);
      reload();
    } catch {
      toast.error("Could not save the game. Please try again.");
    }
    setSaving(false);
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/games/${deleting.id}`);
      toast.success("Game deleted.");
      setDeleting(null);
      reload();
    } catch {
      toast.error("Could not delete the game.");
    }
  };

  if (loading) return <Loading />;

  const resultPill = (g) => {
    if (g.wl === "W") return <span className="font-stat font-bold text-xs px-2.5 py-1 rounded-sm bg-green-100 text-[#166534]">W {g.score}</span>;
    if (g.wl === "L") return <span className="font-stat font-bold text-xs px-2.5 py-1 rounded-sm bg-red-100 text-[#991B1B]">L {g.score}</span>;
    return <span className="font-stat font-bold text-xs px-2.5 py-1 rounded-sm bg-[#F59E0B]/15 text-[#D97706]">TBD</span>;
  };

  return (
    <div data-testid="admin-games-section">
      <SectionHeader
        title="Games & Schedule"
        subtitle="Add games, then after each game click it, pick W or L, enter the score, and hit Save. The website updates right away."
        action={
          <PrimaryBtn onClick={() => setEditing({ ...EMPTY })} data-testid="add-game-button">
            <Plus size={17} /> Add New Game
          </PrimaryBtn>
        }
      />

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="games-list">
        {sorted.length === 0 && <p className="p-8 text-center text-slate-500">No games yet. Click "Add New Game" to schedule the first one.</p>}
        {sorted.map((g) => (
          <div key={g.id} className="flex items-center gap-3 px-4 sm:px-5 py-4 border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`game-row-${g.id}`}>
            <button className="flex-1 min-w-0 text-left" onClick={() => setEditing({ id: g.id, ...toForm(g) })} data-testid={`game-edit-${g.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-bold text-slate-900">{g.ha === "Away" ? "at" : "vs"} {g.opponent}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${g.level === "Varsity" ? "bg-[#D97706]/15 text-[#D97706]" : "bg-slate-100 text-slate-500"}`}>{g.level}</span>
                {resultPill(g)}
              </div>
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                {fmt(g.date)} · {g.time} · <MapPin size={11} /> {g.location}
              </p>
            </button>
            <button onClick={() => setEditing({ id: g.id, ...toForm(g) })} className="p-2.5 text-[#0F4C2A] hover:bg-[#0F4C2A]/10 rounded-md" aria-label="Edit game" data-testid={`game-edit-btn-${g.id}`}>
              <Pencil size={17} />
            </button>
            <button onClick={() => setDeleting(g)} className="p-2.5 text-red-700 hover:bg-red-50 rounded-md" aria-label="Delete game" data-testid={`game-delete-${g.id}`}>
              <Trash2 size={17} />
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Game" : "Add New Game"} onClose={() => setEditing(null)} testid="game-modal">
          <div className="space-y-4">
            <Field label="Opponent" hint="The team we are playing, e.g. Westport">
              <input className={inputCls} value={editing.opponent} onChange={(e) => setEditing({ ...editing, opponent: e.target.value })} placeholder="e.g. Westport" data-testid="game-form-opponent" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Date">
                <input type="date" className={inputCls} value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} data-testid="game-form-date" />
              </Field>
              <Field label="Time">
                <input className={inputCls} value={editing.time} onChange={(e) => setEditing({ ...editing, time: e.target.value })} placeholder="4:00 PM" data-testid="game-form-time" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Team">
                <select className={inputCls} value={editing.level} onChange={(e) => setEditing({ ...editing, level: e.target.value })} data-testid="game-form-level">
                  <option>Varsity</option>
                  <option>JV</option>
                </select>
              </Field>
              <Field label="Home or Away">
                <select className={inputCls} value={editing.ha} onChange={(e) => setEditing({ ...editing, ha: e.target.value })} data-testid="game-form-ha">
                  <option>Home</option>
                  <option>Away</option>
                </select>
              </Field>
            </div>
            <Field label="Location" hint="Where the game is played">
              <input className={inputCls} value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} placeholder="Malmquist Field, Norwalk HS" data-testid="game-form-location" />
            </Field>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <Field label="Result">
                <select className={inputCls} value={editing.wl} onChange={(e) => setEditing({ ...editing, wl: e.target.value })} data-testid="game-form-result">
                  <option value="TBD">Not played yet (TBD)</option>
                  <option value="W">We Won (W)</option>
                  <option value="L">We Lost (L)</option>
                </select>
              </Field>
              {editing.wl !== "TBD" && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <Field label="Our Score">
                    <input type="number" min="0" className={inputCls} value={editing.scoreHome} onChange={(e) => setEditing({ ...editing, scoreHome: e.target.value })} data-testid="game-form-score-us" />
                  </Field>
                  <Field label="Their Score">
                    <input type="number" min="0" className={inputCls} value={editing.scoreAway} onChange={(e) => setEditing({ ...editing, scoreAway: e.target.value })} data-testid="game-form-score-them" />
                  </Field>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <PrimaryBtn className="flex-1" onClick={save} disabled={saving} data-testid="game-form-save">
                {saving ? "Saving..." : "Save Game"}
              </PrimaryBtn>
              <GhostBtn onClick={() => setEditing(null)} data-testid="game-form-cancel">Cancel</GhostBtn>
            </div>
          </div>
        </Modal>
      )}

      {deleting && (
        <ConfirmDialog
          message={`Delete the game vs ${deleting.opponent} on ${fmt(deleting.date)}? This cannot be undone.`}
          onConfirm={remove}
          onCancel={() => setDeleting(null)}
          testid="game-delete-confirm"
        />
      )}
    </div>
  );
}
